import apiClient from '../../shared/services/apiClient';

/**
 * Light App Service (Admin Version)
 *
 * Handles administrative Docker container/stack management operations.
 * Includes: Endpoints (nodes), Templates, Products, Services, Registries, Template Groups
 *
 * All routes use /api/admin/lightapp/* prefix
 */
class LightAppAdminService {
  // ==========================================
  // Endpoint (Node) Management
  // ==========================================

  /**
   * Get all Docker endpoints/nodes
   */
  async getEndpoints(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/lightapp/endpoints', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get endpoint details
   */
  async getEndpoint(id) {
    try {
      const response = await apiClient.get(`/api/admin/lightapp/endpoints/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new endpoint
   */
  async createEndpoint(data) {
    try {
      const response = await apiClient.post('/api/admin/lightapp/endpoints', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update endpoint
   */
  async updateEndpoint(id, data) {
    try {
      const response = await apiClient.put(`/api/admin/lightapp/endpoints/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete endpoint
   */
  async deleteEndpoint(id) {
    try {
      const response = await apiClient.delete(`/api/admin/lightapp/endpoints/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Test endpoint connection
   */
  async testEndpoint(id) {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/endpoints/${id}/test`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Template Management
  // ==========================================

  /**
   * Get all templates
   */
  async getTemplates(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/lightapp/templates', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get template details
   */
  async getTemplate(id) {
    try {
      const response = await apiClient.get(`/api/admin/lightapp/templates/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new template
   */
  async createTemplate(data) {
    try {
      const response = await apiClient.post('/api/admin/lightapp/templates', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update template
   */
  async updateTemplate(id, data) {
    try {
      const response = await apiClient.put(`/api/admin/lightapp/templates/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete template
   */
  async deleteTemplate(id) {
    try {
      const response = await apiClient.delete(`/api/admin/lightapp/templates/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync templates from registry/repository
   */
  async syncTemplates() {
    try {
      const response = await apiClient.post('/api/admin/lightapp/templates/sync');
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Template Group Management
  // ==========================================

  /**
   * Get all template groups
   */
  async getTemplateGroups(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/lightapp/template-groups', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get template group details
   */
  async getTemplateGroup(id) {
    try {
      const response = await apiClient.get(`/api/admin/lightapp/template-groups/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new template group
   */
  async createTemplateGroup(data) {
    try {
      const response = await apiClient.post('/api/admin/lightapp/template-groups', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update template group
   */
  async updateTemplateGroup(id, data) {
    try {
      const response = await apiClient.put(`/api/admin/lightapp/template-groups/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete template group
   */
  async deleteTemplateGroup(id) {
    try {
      const response = await apiClient.delete(`/api/admin/lightapp/template-groups/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Product Management
  // ==========================================

  /**
   * Get all products (paginated)
   */
  async getProducts(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/lightapp/products', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get product details
   */
  async getProduct(productId) {
    try {
      const response = await apiClient.get(`/api/admin/lightapp/products/${productId}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a new product
   */
  async saveProduct(data) {
    try {
      const response = await apiClient.post('/api/admin/lightapp/products', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update an existing product
   */
  async updateProduct(id, data) {
    try {
      const response = await apiClient.put(`/api/admin/lightapp/products/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete a product
   */
  async deleteProduct(id) {
    try {
      const response = await apiClient.delete(`/api/admin/lightapp/products/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create a product from a template
   */
  async createProductFromTemplate(data) {
    try {
      const response = await apiClient.post('/api/admin/lightapp/products/from-template', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Toggle product active status
   */
  async toggleProductActive(id) {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/products/${id}/toggle-active`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Registry Management
  // ==========================================

  /**
   * Get all Docker registries
   */
  async getRegistries(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/lightapp/registries', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new registry
   */
  async createRegistry(data) {
    try {
      const response = await apiClient.post('/api/admin/lightapp/registries', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Update registry
   */
  async updateRegistry(id, data) {
    try {
      const response = await apiClient.put(`/api/admin/lightapp/registries/${id}`, data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Delete registry
   */
  async deleteRegistry(id) {
    try {
      const response = await apiClient.delete(`/api/admin/lightapp/registries/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  // ==========================================
  // Service Management (Admin view of all services)
  // ==========================================

  /**
   * Get all services (admin can see all users' services)
   */
  async getServices(params = {}) {
    try {
      const response = await apiClient.get('/api/admin/lightapp/services', { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get service details
   */
  async getService(id) {
    try {
      const response = await apiClient.get(`/api/admin/lightapp/services/${id}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Create new service (manual deployment)
   */
  async createService(data) {
    try {
      const response = await apiClient.post('/api/admin/lightapp/services', data);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Perform service action (start, stop, restart, etc.)
   */
  async serviceAction(id, action) {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/services/${id}/${action}`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Reinstall service
   */
  async reinstallService(id) {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/services/${id}/reinstall`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Terminate service
   */
  async terminateService(id) {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/services/${id}/terminate`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get container logs for a service
   */
  async getServiceLogs(id, params = {}) {
    try {
      const response = await apiClient.get(`/api/admin/lightapp/services/${id}/logs`, { params });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Get child containers of a stack service
   */
  async getServiceContainers(id) {
    try {
      const response = await apiClient.get(`/api/admin/lightapp/services/${id}/containers`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Perform an action on a stack child container
   */
  async containerAction(id, containerId, action) {
    try {
      const response = await apiClient.post(
        `/api/admin/lightapp/services/${id}/containers/${containerId}/${action}`
      );
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Sync stack containers from the agent
   */
  async syncServiceContainers(id) {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/services/${id}/sync-containers`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Suspend a service (admin only)
   */
  async suspendService(id, reason = '') {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/services/${id}/suspend`, { reason });
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }

  /**
   * Unsuspend a service (admin only)
   */
  async unsuspendService(id) {
    try {
      const response = await apiClient.post(`/api/admin/lightapp/services/${id}/unsuspend`);
      return response.data;
    } catch (error) {
      return { success: false, error: error.response?.data?.error || error.message };
    }
  }
}

export default new LightAppAdminService();
